import React, { useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../config';

export default function HoratioPanel() {
  const [about, setAbout] = useState('');
  const [query, setQuery] = useState('');
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [cards, setCards] = useState([]);
  const [showKeyExamples, setShowKeyExamples] = useState(false);
  const endRef = useRef(null);
  useEffect(() => {
    try { if (endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' }); } catch {}
  }, [messages, reply, suggestions, busy]);
  // Minimal inline formatter: *Title* -> <em>Title</em>
  const renderInline = (text) => {
    try {
      const t = String(text || '');
      const parts = [];
      const re = /\*([^*]+)\*/g;
      let last = 0; let m; let k = 0;
      while ((m = re.exec(t)) !== null) {
        if (m.index > last) parts.push(t.slice(last, m.index));
        parts.push(<em key={`em-${k++}`}>{m[1]}</em>);
        last = re.lastIndex;
      }
      if (last < t.length) parts.push(t.slice(last));
      return parts;
    } catch { return String(text || ''); }
  };

  const send = async (overrideText) => {
    if (busy) return;
    // Guard: if onClick passed the event object, ignore it and use current query
    if (overrideText && typeof overrideText !== 'string') {
      overrideText = undefined;
    }
    let raw = overrideText !== undefined ? overrideText : (query || '');
    const q = String(raw).trim();
    if (!q) return;
    try {
      setBusy(true);
      // Append to conversation and keep history
      setReply('');
      setSuggestions([]);
      setMessages(prev => [...prev, { role: 'user', content: q }]);
      const body = { messages: [...messages, { role:'user', content: q }], hints: { intent: 'shakespeare' } };
      const r = await fetch(`${API_BASE_URL}/api/chat`, { method:'POST', headers:{ 'Content-Type':'application/json' }, credentials:'include', body: JSON.stringify(body) });
      if (!r.ok) {
        const txt = await r.text();
        const msg = `Chat error (${r.status}): ${txt.slice(0,140)}`;
        setReply(msg);
        setMessages(prev => [...prev, { role: 'assistant', content: msg }]);
        setSuggestions([]);
        setCards([]);
        setQuery('');
        return;
      }
      const dj = await r.json();
      const rep = (dj && dj.reply) || '';
      setReply(rep);
      setMessages(prev => [...prev, { role: 'assistant', content: rep }]);
      setSuggestions(Array.isArray(dj && dj.suggestions) ? dj.suggestions : []);
      setCards(Array.isArray(dj && dj.cards) ? dj.cards : []);
      // Clear input after a completed exchange
      setQuery('');
    } catch {
      setReply('');
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    // Ensure auth cookie (JWT) is minted for this origin
    (async () => {
      try {
        await fetch(`${API_BASE_URL}/api/auth/token`, { method:'POST', credentials:'include' });
      } catch {}
    })();
    // Pull brief framing text from the backend if available, else static copy
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/insights/horatio-about`);
        if (r.ok) {
          const dj = await r.json();
          if (alive) setAbout(dj.about || '');
        }
      } catch {}
    })();
    return () => { alive = false };
  }, []);
  const fallback = "Horatio uses a key–example system to answer questions about Shakespeare. Horatio may decline other topics.";
  const text = about || fallback;
  return (
    <div className="console-container" style={{ background:'#000', color:'#fff' }}>
      <div className="console-left" style={{ width: '100%' }}>
        <div className="console-panel" style={{ background:'#000', color:'#fff' }}>
          <div className="line" style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ whiteSpace: 'pre-wrap', color:'#ddd' }}>{text}</span>
            <button onClick={()=>setShowKeyExamples(v=>!v)} className="btn-dark" style={{ padding:'2px 8px', fontSize: 12 }}>
              {showKeyExamples ? 'Hide' : 'Key‑Example System'}
            </button>
          </div>
          {showKeyExamples && (
            <div className="line" style={{ color:'#e6eef6', background:'#0a0a0a', border:'1px solid #222', borderRadius:6, padding:'8px 10px', marginTop:6 }}>
              <div className="line" style={{ marginBottom: 6 }}>
                <strong>Ideas</strong>, inspired by linguist George Lakoff and historian Hans Blumenberg, are short analogies that give shape to large human experiences. They work as analogical frames that structure inference—compact comparisons that guide how we read and connect moments. <em>Examples</em>: “Time is a river”; “Hope is a bird.”
              </div>
              <div className="line" style={{ marginBottom: 6 }}>
                <strong>Images</strong> are concrete nouns—language shards that connect moments across the plays. They act as stable lexical anchors (like “ring,” “candle,” “bear”) that recur and carry local associations.
              </div>
              <div className="line" style={{ marginBottom: 8 }}>
                <strong>Motives</strong> describe how an image is set in motion or used—the program an image plays onstage (binding, testing, tempting, revealing).
              </div>
              <div className="line" style={{ marginBottom: 4, opacity: .95 }}>
                <strong>Example</strong>: Image = <em>RING</em>. Motive: “ring seals vows and binds fortune.” In <em>Merchant of Venice</em> and <em>Othello</em>, rings (and ring‑like tokens) seal promises and test fidelity; we index such passages as exemplars. In our system, rings and their motive help explain a post/scene’s Shakespearean fit (used in retrieval, scoring, and the Immortalize rationale).
              </div>
            </div>
          )}
          {/* Input moved to top */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '8px 0 10px 0' }}>
            <input
              value={query}
              onChange={e=>setQuery(e.target.value)}
              onKeyDown={(e)=>{
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              placeholder="Ask about Shakespeare (e.g., ocular proof)"
              style={{ flex:1, padding:'6px 8px', background:'#0a0a0a', color:'#fff', border:'1px solid #333' }}
            />
            <button disabled={busy || !query.trim()} onClick={() => send()} className="btn-dark">{busy ? 'Asking…' : 'Ask'}</button>
          </div>
          <div style={{ maxHeight: 520, overflowY: 'auto', border: '1px solid #222', borderRadius: 6, padding: 10, background: '#0a0a0a' }}>
            {messages.map((m, idx) => (
              <div key={idx} className="line" style={{ whiteSpace:'pre-wrap', margin: '6px 0', color:'#f3f4f6' }}>
                <span style={{ fontWeight: 600, color: m.role === 'user' ? '#60a5fa' : '#9bdcff' }}>{m.role === 'user' ? 'You' : 'Horatio'}:</span> {m.role === 'assistant' ? renderInline(m.content) : String(m.content || '')}
              </div>
            ))}
            <div ref={endRef} />
          </div>
          {suggestions && suggestions.length > 0 && (
            <div className="line" style={{ marginTop: 10 }}>
              {suggestions.map((sug, i) => (
                <button key={i} onClick={() => {
                  try {
                    const cleaned = String(sug.query || '').replace(/\s+in\s+[A-Za-z][A-Za-z\s]+$/i, '').trim();
                    send(cleaned || String(sug.query || ''));
                  } catch {
                    send(String(sug.query || ''));
                  }
                }} className="btn-dark" style={{ marginRight: 8, marginBottom: 6 }}>
                  {sug.label || sug.query}
                </button>
              ))}
              <button onClick={() => send('More examples')} className="btn-dark" style={{ marginLeft: 4 }}>More examples</button>
            </div>
          )}
          {/* Motive cards row (optional, below conversation) */}
          {cards && cards.length > 0 && (
            <div className="line" style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 600, color:'#9bdcff', marginBottom: 6 }}>Related Motive Cards</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap: 8 }}>
                {cards.map((c, i) => (
                  <div key={i} style={{ border:'1px solid #222', borderRadius:6, padding:'8px 10px', background:'#0a0a0a', color:'#e6eef6', minWidth: 240 }}>
                    <div style={{ color:'#ffb2e4', fontWeight:700 }}>{c.header}</div>
                    {(c.quotes||[]).map((q, j) => (
                      <div key={j} style={{ fontSize: 12, opacity: .9, marginTop: 4 }}>{q}</div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
